import { useState, useEffect, useRef, useCallback } from 'react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * useJobStream — SSE hook for real-time pipeline progress.
 * Falls back to polling if SSE fails or is unsupported.
 *
 * @param {string} jobId
 * @param {object} options
 * @param {boolean} options.enabled - Whether streaming is active
 * @param {function} options.onComplete - Called when job reaches terminal state
 */
export function useJobStream(jobId, { enabled = true, onComplete } = {}) {
  const [progress, setProgress] = useState(null);
  const [connected, setConnected] = useState(false);
  const [usingSSE, setUsingSSE] = useState(false);
  const esRef = useRef(null);
  const pollingRef = useRef(null);
  const reconnectTimer = useRef(null);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const isTerminal = useCallback((status) => {
    return ['review', 'completed', 'synced', 'failed', 'sync_ready', 'sync_complete'].includes(status);
  }, []);

  // Merge a page_progress event into the existing progress snapshot
  const applyPageUpdate = useCallback((prev, pageData) => {
    if (!prev) return prev;
    const pages = [...(prev.pages || [])];
    const idx = pages.findIndex(p => p.page_index === pageData.page_index);
    if (idx >= 0) {
      pages[idx] = { ...pages[idx], ...pageData };
    } else {
      pages.push(pageData);
      pages.sort((a, b) => a.page_index - b.page_index);
    }
    const done = pages.filter(p => ['done', 'skipped'].includes(p.status)).length;
    const errors = pages.filter(p => p.status === 'error').length;
    const total = Math.max(prev.total_pages || 0, pages.length);
    return {
      ...prev,
      pages,
      pages_done: done,
      pages_errored: errors,
      percent_complete: total > 0 ? Math.round(done / total * 1000) / 10 : 0,
    };
  }, []);

  // Apply a stage_change event
  const applyStageChange = useCallback((prev, stageData) => {
    if (!prev) return prev;
    const updated = { ...prev, ...stageData };
    return updated;
  }, []);

  // SSE connection
  useEffect(() => {
    if (!jobId || !enabled) return;

    const token = localStorage.getItem('fmiq_token');
    if (!token) return;

    const url = `${API_URL}/drawing-jobs/${jobId}/progress/stream?token=${encodeURIComponent(token)}`;
    let es;

    try {
      es = new EventSource(url);
      esRef.current = es;
    } catch {
      // SSE not supported, will fall back to polling
      return;
    }

    es.addEventListener('snapshot', (e) => {
      try {
        const data = JSON.parse(e.data);
        setProgress(data);
        setConnected(true);
        setUsingSSE(true);
        if (isTerminal(data.status)) {
          onCompleteRef.current?.(data.status);
          es.close();
        }
      } catch {}
    });

    es.addEventListener('page_progress', (e) => {
      try {
        const data = JSON.parse(e.data);
        setProgress(prev => applyPageUpdate(prev, data));
      } catch {}
    });

    es.addEventListener('stage_change', (e) => {
      try {
        const data = JSON.parse(e.data);
        setProgress(prev => applyStageChange(prev, data));
        if (isTerminal(data.status)) {
          onCompleteRef.current?.(data.status);
          es.close();
        }
      } catch {}
    });

    es.addEventListener('label_progress', (e) => {
      try {
        const data = JSON.parse(e.data);
        setProgress(prev => prev ? { ...prev, labeling: data } : prev);
      } catch {}
    });

    es.addEventListener('label_complete', (e) => {
      try {
        const data = JSON.parse(e.data);
        setProgress(prev => prev ? { ...prev, labeling: { ...data, running: false } } : prev);
      } catch {}
    });

    es.onerror = () => {
      setConnected(false);
      setUsingSSE(false);
      es.close();
      // Attempt reconnect after 3s
      reconnectTimer.current = setTimeout(() => {
        // Fall through to polling via the effect re-running
      }, 3000);
    };

    return () => {
      es.close();
      esRef.current = null;
      clearTimeout(reconnectTimer.current);
    };
  }, [jobId, enabled, isTerminal, applyPageUpdate, applyStageChange]);

  // Polling fallback
  useEffect(() => {
    if (!jobId || !enabled || usingSSE) {
      clearInterval(pollingRef.current);
      return;
    }

    const token = localStorage.getItem('fmiq_token');
    if (!token) return;

    const fetchProgress = async () => {
      try {
        const res = await fetch(`${API_URL}/drawing-jobs/${jobId}/progress`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        setProgress(data);
        setConnected(true);
        if (isTerminal(data.status)) {
          clearInterval(pollingRef.current);
          onCompleteRef.current?.(data.status);
        }
      } catch {}
    };

    fetchProgress();
    pollingRef.current = setInterval(fetchProgress, 2500);
    return () => clearInterval(pollingRef.current);
  }, [jobId, enabled, usingSSE, isTerminal]);

  return { progress, connected, usingSSE };
}
